import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import { Map } from 'immutable';

import Container        from '@mui/material/Container';
import Box              from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography       from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import CampaignRecord from 'records/campaign_record';
import Display        from 'components/Generic/Display';
import { useLazyQuery }                  from '@apollo/client';
import { CAMPAIGN_RECORD_FETCH_LISTING } from 'queries/campaign/fetch';
import SearchBar      from './SearchBar';
import Paginator      from './Paginator';
import List           from './List';

function Content({ locale }) {
  const theme = useTheme();

  const [state, setState] = useState(new Map());
  const page      = state.get('page', 1);
  const pages     = state.get('pages', 1);
  const total     = state.get('total', 0);
  const campaigns = state.get('campaigns', []);

  const query      = state.get('query', '');
  const queryParam = state.get('queryParam', '');

  const address      = state.get('address', '');
  const addressParam = state.get('addressParam', '');

  const latlng      = state.get('latlng', null);
  const country     = state.get('country', '');

  const remote      = state.get('remote', false);
  const remoteParam = state.get('remoteParam', false);

  const queryvar = {
    search: {
      where:       [{ field: 'is_receiving_applications', value: 'true' }, { field: 'on_listing', value: 'true' }],
      cloudsearch: {
        query:    queryParam,
        location: addressParam,
        remote:   remoteParam,
        country:  country,
        lat_lng:  latlng,
      },
    },
    page,
    limit: 10,
  };

  const [fetchCampaigns, { loading, data }] = useLazyQuery(
    CAMPAIGN_RECORD_FETCH_LISTING,
    { variables: queryvar },
  );

  useEffect(() => {
    fetchCampaigns({ variables: queryvar });
  }, [page, queryParam, addressParam, remoteParam]);

  useEffect(() => {
    if (!data) return;

    const records = data.campaign.records.collection.map((json) => new CampaignRecord(json));

    setState(state.set('pages', data.campaign.records.metadata.totalPages).set('total', data.campaign.records.metadata.totalCount).set('campaigns', records));
  }, [data]);

  const onInputChange = (name, value) => {
    setState(state.set(name, value));
  };

  const onPlaceSelect = (value) => {
    const newState = state.set('address', value.formatted_address).set('country', value.country);

    if (value.locality) { // City or country is too big to send the lat/lng
      setState(newState.set('latlng', { latitude: value.latitude, longitude: value.longitude }));
      return
    }

    // If only the city or only the country, we only send the country and the address without lat/lng
    setState(newState.set('latlng', null));
  };

  const search = () => {
    setState(state.set('page', 1).set('queryParam', query).set('addressParam', address).set('remoteParam', remote));
  };

  return (
    <Container sx={{ mb: 6, [theme.breakpoints.down('sm')]: { mb: 3 } }}>
      <Box sx={{ pt: 4 }}>
        <SearchBar query={query} address={address} remote={remote} onPlaceSelect={onPlaceSelect} onChange={onInputChange} search={search} locale={locale} />
      </Box>

      <Display if={campaigns.length}>
        <Box sx={{ pt: 2 }}>
          <Paginator page={page} pages={pages} total={total} showCount count={campaigns.length} onChange={onInputChange} />
        </Box>
      </Display>

      <Display if={!loading}>
        <Display if={campaigns.length}>
          <Box sx={{ pt: 1 }}>
            <List campaigns={campaigns} />
          </Box>
        </Display>

        <Display if={!campaigns.length}>
          <Box sx={{
            display: 'flex', height: '20vh', alignContent: 'center', alignItems: 'center', justifyContent: 'center',
          }}
          >
            <Typography sx={{ fontStyle: 'italic' }}>{t('eos.company.listings.page.label.no_results')}</Typography>
          </Box>
        </Display>
      </Display>

      <Display if={loading}>
        <Box sx={{
          display: 'flex', height: '20vh', alignContent: 'center', alignItems: 'center', justifyContent: 'center',
        }}
        >
          <CircularProgress />
        </Box>
      </Display>

      <Display if={campaigns.length}>
        <Box sx={{ pt: 2 }}>
          <Paginator page={page} pages={pages} total={total} showCount={false} count={campaigns.length} onChange={onInputChange} />
        </Box>
      </Display>
    </Container>
  );
}

Content.defaultProps = {
  locale: 'en',
};

Content.propTypes = {
  locale: PropTypes.string,
};

export default Content;
